import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {
  Autocomplete,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import {
  every,
  filter,
  flatMap,
  groupBy,
  includes,
  isEmpty,
  keys,
  map,
  some,
  sortBy,
  take,
  toLower,
  uniq,
} from 'lodash';

import { Dropdown, RowIdentifier } from '../../../types/models';
import CenteredSpinner from '../../shared/CenteredSpinner';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface IFilterDimAndInstStepProps {
  rowIdentifiers?: RowIdentifier[];
  isLoading?: boolean;
  selectedNames: Record<string, boolean>;
  onChange: (selectedNames: Record<string, boolean>) => void;
}

interface IReportDimension {
  name: string;
  values: string[];
}

// One row per Dimension row identifier (in DimNumber order), with the names of the DimensionInstances that share its DimNumber.
// Dimensions without any instance are dropped since there is nothing to filter on.
const buildReportDimensions = (
  rowIdentifiers?: RowIdentifier[]
): IReportDimension[] => {
  const instancesByDimNumber = groupBy(
    filter(rowIdentifiers, { Type: 'DimensionInstance' }),
    'DimNumber'
  );

  return filter(
    map(
      sortBy(filter(rowIdentifiers, { Type: 'Dimension' }), 'DimNumber'),
      (dimension) => ({
        name: dimension.Name,
        values: uniq(
          map(instancesByDimNumber[`${dimension.DimNumber}`], 'Name')
        ),
      })
    ),
    (dimension) => !isEmpty(dimension.values)
  );
};

// Selection keys stay in the `Header>Value` shape filterInputsBySelection expects
const toPath = (dimensionName: string, value: string): string =>
  `${dimensionName}>${value}`;

export const getCheckedNames = (
  selectedNames: Record<string, boolean>
): string[] => keys(selectedNames).filter((name) => selectedNames[name]);

// Names of the mandatory report dimensions that have no instance checked. Only the first dimension
// is mandatory; an empty later one simply applies no filter (see filterSeriesBySelection)
export const getEmptyDimensionNames = (
  rowIdentifiers: RowIdentifier[] | undefined,
  selectedNames: Record<string, boolean>
): string[] =>
  map(
    filter(
      take(buildReportDimensions(rowIdentifiers), 1),
      (dimension) =>
        !some(
          dimension.values,
          (value) => selectedNames[toPath(dimension.name, value)]
        )
    ),
    'name'
  );

interface IDimensionValuesMultiSelectProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
}

// Same look and Select All behaviour as Reports/DimensionInstancesMultiSelect, but over plain string values
const DimensionValuesMultiSelect: FunctionComponent<
  IDimensionValuesMultiSelectProps
> = ({ options, value, onChange }) => {
  const [inputValue, setInputValue] = useState('');

  const filterOptions = (allOptions: string[], search: string) =>
    search === ''
      ? allOptions
      : filter(allOptions, (option) =>
          includes(toLower(option), toLower(search))
        );

  const visibleOptions = filterOptions(options, inputValue);
  const allVisibleSelected =
    !isEmpty(visibleOptions) &&
    every(visibleOptions, (option) => includes(value, option));

  // When every option is picked, collapse the tags into a single "All" pill
  const allOptionsSelected =
    !isEmpty(options) && every(options, (option) => includes(value, option));

  const handleChange = (newValue: string[]) => {
    if (!includes(newValue, Dropdown.Select_All)) {
      onChange(newValue);
      return;
    }
    // Select All toggles only what the current search shows, keeping anything else already picked
    onChange(
      allVisibleSelected
        ? filter(value, (v) => !includes(visibleOptions, v))
        : uniq([...value, ...visibleOptions])
    );
    setInputValue('');
  };

  return (
    <Autocomplete<string, true>
      limitTags={1}
      multiple
      value={value}
      onChange={(_, newValue) => handleChange(newValue)}
      inputValue={inputValue}
      // Autocomplete resets the search after every pick, so the text clears once a selection is made
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      disableCloseOnSelect
      ChipProps={{ size: 'small' }}
      renderTags={
        allOptionsSelected
          ? (_, getTagProps) => {
              const { key, ...tagProps } = getTagProps({ index: 0 });
              return (
                <Chip
                  {...tagProps}
                  key={key}
                  label="All"
                  size="small"
                  onDelete={() => onChange([])}
                />
              );
            }
          : undefined
      }
      options={options}
      filterOptions={(allOptions, params) => {
        const filtered = filterOptions(allOptions, params.inputValue);
        return isEmpty(filtered) ? [] : [Dropdown.Select_All, ...filtered];
      }}
      renderOption={(props, option, { selected }) => {
        const parts = parse(
          option,
          match(option, inputValue, {
            insideWords: true,
            findAllOccurrences: true,
          })
        );
        return (
          <Typography component="li" {...props} key={option} noWrap>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={
                option === Dropdown.Select_All ? allVisibleSelected : selected
              }
              indeterminate={
                option === Dropdown.Select_All &&
                !allVisibleSelected &&
                some(visibleOptions, (o) => includes(value, o))
              }
            />
            {map(parts, (part, index) => (
              <span
                key={index}
                style={{
                  fontWeight: part.highlight ? 700 : 400,
                  whiteSpace: 'pre',
                }}
              >
                {part.text}
              </span>
            ))}
          </Typography>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Dimension Instances"
          fullWidth
          size="small"
        />
      )}
    />
  );
};

const FilterDimAndInstStep: FunctionComponent<IFilterDimAndInstStepProps> = ({
  rowIdentifiers,
  isLoading,
  selectedNames,
  onChange,
}) => {
  const reportDimensions = useMemo(
    () => buildReportDimensions(rowIdentifiers),
    [rowIdentifiers]
  );

  const dataReady = !isLoading && !isEmpty(reportDimensions);

  // Select every value whenever the data (re)loads, since it starts out empty
  useEffect(() => {
    if (dataReady) {
      onChange(
        Object.fromEntries(
          flatMap(reportDimensions, (dimension) =>
            map(dimension.values, (value) => [
              toPath(dimension.name, value),
              true,
            ])
          )
        )
      );
    }
  }, [reportDimensions, dataReady]);

  const handleDimensionValuesChange = (
    dimension: IReportDimension,
    selectedValues: string[]
  ): void => {
    onChange({
      ...selectedNames,
      ...Object.fromEntries(
        map(dimension.values, (value) => [
          toPath(dimension.name, value),
          includes(selectedValues, value),
        ])
      ),
    });
  };

  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Define Report filters
      </Typography>
      {dataReady ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Report Dimensions</TableCell>
              <TableCell>Dimension Instance Filter</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(reportDimensions, (dimension, index) => (
              <TableRow key={dimension.name}>
                <TableCell component="th" scope="row">
                  {index + 1}
                </TableCell>
                <TableCell>{dimension.name}</TableCell>
                <TableCell
                  sx={{
                    width: '60%',
                    maxWidth: '60%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <DimensionValuesMultiSelect
                    options={dimension.values}
                    value={filter(
                      dimension.values,
                      (value) => selectedNames[toPath(dimension.name, value)]
                    )}
                    onChange={(selectedValues) =>
                      handleDimensionValuesChange(dimension, selectedValues)
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <CenteredSpinner />
      )}
    </>
  );
};

export default FilterDimAndInstStep;
